import React, { useContext, useEffect } from 'react'
import {
  Badge, Box, Button, Flex, Heading, Image, Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger, Switch, Text, Stack
} from '@chakra-ui/react'

import { logos } from '../../utils/companiesLogos'
import { Duplicate, Refresh } from '../../icons'
import { offerStatusEnum } from 'BrokerTsLib/src/enums/offerStatusEnum'
import { useClone, useSetOfferIncluded } from '../../hooks/useOffer'
import { Offer, Document, OfferVehicle } from 'brokertslib/src/dtos'
import { MenuItem, MenuItemWithSubMenu, SubMenu } from '../../components/Menu'
import { useGetOfferDocuments } from '../../hooks/useDocuments'
import moment from 'moment'
import { IOfferCompany } from 'BrokerTsLib/src/enums'
import { carkulkaOffersContext } from '../../context/CarkulkaOffersProvider'

const documentUrl = `${process.env.REACT_APP_DOCUMENT_VEHICLE_API}`

const OfferHeaderButtons = ({ children }: { children: React.ReactNode }) => {
  return <Box sx={{ display: 'flex', gap: { base: 1, md: 2 } }}>{children}</Box>
}

const OfferHeaderButtonLabel = ({ children }: { children: React.ReactNode }) => {
  return <Text sx={{ display: { base: 'none', md: 'inline' } }}>{children}</Text>
}
interface OfferHeaderProps {
  displayId: string
  company: IOfferCompany
  status: offerStatusEnum
  color: string,
  offer: OfferVehicle,
  isDisabled?: boolean,
  onToggle?: (included: boolean) => void,
  isVisible: boolean
}

export const OfferHeader = ({ displayId, company, status, color, offer, isDisabled = false, onToggle, isVisible = true, }: OfferHeaderProps) => {

  const { updateOffer, recalculateOffers: recalculate, calculatingOffersIds } = useContext(carkulkaOffersContext)
  const isRecalculating = calculatingOffersIds?.includes(offer!.offerId) ?? false
  const { mutateAsync: cloneOffer } = useClone(offer.offerId)
  const { refetch: getOfferDocuments, data: offerDocuments } = useGetOfferDocuments(offer!.offerId, { enabled: false })

  // doctu nove dokumenty po kazde rekalkulaci
  useEffect(() => {
    if (!isRecalculating)
      setTimeout(() => {
        getOfferDocuments() // at nebrani nacteni jinym dulezitejsim requestum
      }, 5000)
  }, [isRecalculating]);

  const { mutateAsync: setIncluded } = useSetOfferIncluded()

  const handleToggle = ((included: boolean) => {
    onToggle && onToggle(included)

    setTimeout(() => {
      offer!.included = included
      updateOffer(offer!)
      setIncluded({ offerId: offer?.offerId!, included: included })
    }, 300)
  })

  return (
    <Box
      sx={{
        display: { base: 'grid', sm: 'flex' },
        alignContent: { base: 'stretch', sm: 'center' },
        justifyContent: { base: 'strecth', sm: 'space-between' },
        columnGap: 4,
        rowGap: 2,
        py: 2,
        px: 4,
        bgColor: color,
        borderBottom: color === 'white' ? '1px solid' : 'none',
        borderBottomColor: 'gray.200',
        borderTopRadius: 'md',
      }}
      as="header"
    >
      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          columnGap: { base: 4, lg: 8 },
        }}
      >
        <Flex sx={{ alignItems: 'center', columnGap: { base: 4, lg: 8 } }}>
          <Image
            src={logos[company.id]}
            alt={company.name}
            width="64px"
            height="48px"
            style={{
              mixBlendMode: 'multiply',
              pointerEvents: 'none',
            }}
          />

          <Heading as="h2" sx={{ fontSize: { base: 'sm', lg: 'md' } }}>
            {company.name}
          </Heading>
        </Flex>

        {/* {status !== 'error' && <Stars />} */}

        {status === 'error' && <Badge colorScheme="red">
          <Popover trigger='click'>
            <PopoverTrigger>
              <Text cursor='pointer'>Chyba kalkulace</Text>
            </PopoverTrigger>
            <PopoverContent w={'auto'} maxW={300}>
              <Stack direction={'column'}>
                {offer?.errors.map((e, i) => {
                  return (
                    <Text key={i} variant='calcError' whiteSpace={'break-spaces'}>{e.message}</Text>
                  )
                })}
              </Stack>
            </PopoverContent>
          </Popover>
        </Badge>
        }

        {status === 'selected' && <Badge colorScheme="green">Vybraná nabídka</Badge>}

        {(offer?.infos && offer.infos.length > 0 || offer?.warnings && offer.warnings.length > 0 || offer?.underwriting?.required) && (
          <Popover>
            <PopoverTrigger>
              <Badge sx={{ cursor: 'pointer' }} colorScheme="green" variant="solid">
                Info
              </Badge>
            </PopoverTrigger>

            <PopoverContent>
              <PopoverArrow />
              <PopoverBody sx={{ display: 'grid', gap: 2 }}>
                {
                  <Box display='grid' gap={2} dangerouslySetInnerHTML={{
                    __html: [
                      ...(offer?.underwriting?.message ? [offer.underwriting.message] : []),
                      ...(offer!.infos ?? []).map(x => x.message),
                      ...(offer!.warnings ?? []).map(x => x.message)].join("<hr/>")
                  }}></Box>
                }
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </Flex>

      <Flex sx={{ alignItems: 'center', justifyContent: 'space-between', gap: { base: 2, lg: 8 } }}>
        <OfferHeaderButtons>
          {!isDisabled &&
            <Button variant="outline" size="sm" onClick={() => {
              cloneOffer()
                .then(({ data: offer }) => {
                  updateOffer(offer as OfferVehicle)
                  recalculate([offer.offerId], true)
                })
            }}>
              <Duplicate color="secondary.500" />
              <OfferHeaderButtonLabel>Klonovat</OfferHeaderButtonLabel>
            </Button>
          }

          {(offer?.offerId && offer.offerId > 0) && !isDisabled &&
            <Button
              variant="outline"
              size="sm"
              isDisabled={isRecalculating}
              isLoading={isRecalculating}
              loadingText="Přepočítávám..."
              onClick={() => {
                recalculate([offer?.offerId ?? 0], true)
                  .finally(() => {
                    getOfferDocuments()
                  })
              }}>
              <Refresh color="secondary.500" />
              <OfferHeaderButtonLabel>Přepočítat</OfferHeaderButtonLabel>
            </Button>
          }

          {(offerDocuments?.data && offerDocuments.data.length > 0) &&
            <MenuItemWithSubMenu trigger='click'
              button={
                <MenuItem variant="outline" colorScheme='gray' color="gray.500">
                  <OfferHeaderButtonLabel>XML</OfferHeaderButtonLabel>
                </MenuItem>
              }
              subMenu={
                <SubMenu sx={{
                  maxHeight: "300px",
                  overflow: "auto",
                  p: 2,
                  width: 'max-content',
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  bg: 'white',
                  borderRadius: 'md',
                  boxShadow: 'md',
                  listStyle: 'none',
                  zIndex: 'subMenu'
                }}
                >
                  <li>
                    {offerDocuments?.data.map((doc: Document) => {
                      return (
                        <MenuItem key={doc.id} target='_blank' href={`${documentUrl}/GetOfferDocument?docId=${doc.id}`}><>{doc.name} ({moment(doc.date).format('D.M.yyyy H:mm:ss')})</></MenuItem>
                      )
                    })}
                  </li>
                </SubMenu>
              }
            />
          }
        </OfferHeaderButtons>

        <Flex sx={{ gap: { base: 4, lg: 8 } }}>
          {displayId && (
            <Text sx={{ fontWeight: 'semibold', justifySelf: 'end' }} as="p">
              {displayId}
            </Text>
          )}

          <Switch
            isChecked={offer?.included}
            id={`offer-toggle-${displayId}`}
            size="md"
            onChange={(e) => handleToggle(e.target.checked)}
            isDisabled={isDisabled}
          // isChecked={isVisible}
          // onChange={handleToggle}
          />
        </Flex>
      </Flex>
    </Box >
  )
}
